import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { useSmsSenders } from '@/features/campaigns/v2/Sms/hooks/useSmsSenders';
import { useSmsTemplates } from '@/features/campaigns/v2/Sms/hooks/useSmsTemplates';
import { isSmsSenderRoute } from '@/features/campaigns/v2/Sms/smsWorkspaceRoutes';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export type SmsWorkspaceContextValue = {
  connectorId: string;
};

type SmsTemplatesState = ReturnType<typeof useSmsTemplates>;
type SmsSendersState = ReturnType<typeof useSmsSenders>;

export type SmsWorkspaceDataContextValue = {
  templates: SmsTemplatesState;
  senders: SmsSendersState;
  /** False until a sender route is visited; senders prepare is skipped until then. */
  hasEnabledSenders: boolean;
};

const SmsWorkspaceContext = createContext<SmsWorkspaceContextValue | null>(null);
const SmsWorkspaceDataContext = createContext<SmsWorkspaceDataContextValue | null>(null);

const SmsWorkspaceDataProvider: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const { isAudienceSettled, hasAudience } = useFirstAudienceId();
  const skipForMissingAudience = isAudienceSettled && !hasAudience;

  const [hasEnabledSenders, setHasEnabledSenders] = useState(() => isSmsSenderRoute(location.pathname));

  useEffect(() => {
    if (isSmsSenderRoute(location.pathname)) {
      setHasEnabledSenders(true);
    }
  }, [location.pathname]);

  const templates = useSmsTemplates({ skip: skipForMissingAudience });
  const senders = useSmsSenders({ skip: skipForMissingAudience || !hasEnabledSenders });

  const value = useMemo<SmsWorkspaceDataContextValue>(
    () => ({
      templates,
      senders,
      hasEnabledSenders,
    }),
    [hasEnabledSenders, senders, templates]
  );

  return <SmsWorkspaceDataContext.Provider value={value}>{children}</SmsWorkspaceDataContext.Provider>;
};

export const SmsWorkspaceProvider: FC<PropsWithChildren<{ connectorId: string }>> = ({ connectorId, children }) => {
  const value = useMemo<SmsWorkspaceContextValue>(() => ({ connectorId }), [connectorId]);

  return (
    <SmsWorkspaceContext.Provider value={value}>
      <SmsWorkspaceDataProvider>{children}</SmsWorkspaceDataProvider>
    </SmsWorkspaceContext.Provider>
  );
};

export const useSmsWorkspace = (): SmsWorkspaceContextValue => {
  const context = useContext(SmsWorkspaceContext);
  if (!context) {
    throw new Error('useSmsWorkspace must be used within SmsWorkspaceProvider');
  }
  return context;
};

export const useSmsWorkspaceData = (): SmsWorkspaceDataContextValue => {
  const context = useContext(SmsWorkspaceDataContext);
  if (!context) {
    throw new Error('useSmsWorkspaceData must be used within SmsWorkspaceProvider');
  }
  return context;
};

export const useSmsWorkspaceTemplates = (): SmsTemplatesState => useSmsWorkspaceData().templates;

export const useSmsWorkspaceSenders = (): SmsSendersState => useSmsWorkspaceData().senders;
